///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserAuthorities } from '../../../services/user/user-authorities';
import { _cloneDeep } from '../../../shared/utils';
import { Principal } from '../../../shared/auth/principal.service';
import { GtthFolderDialogComponent } from '../../../projects/dialogs/folder-dialog/folder-dialog.component';
import { ItemDirection, SuiTypeRadio, SuiTypeString } from '@siklu/sui-lib/infrastructure';
import { Store } from '@ngrx/store';
import { closeUserDialog, createUser, modifyUser } from '../../store/users-list.actions';
import { AbstractDialogComponent } from '../../../dialog-manager/abstract-dialog.component';
import { SuiManagedUserVM, SuiUserDTO } from '../../../generated/shared-sui-types.model';
import { ManagedUserVM } from '../../../generated/shared-types.model';
import { Actions, ofType } from '@ngrx/effects';
import { LoaderIds, PreloaderService } from '../../../services/preloader.service';
import { createPasswordFieldsPair } from '../../../shared/password-field.generator';
import { isDefined } from '@siklu/sui-lib/utils';

@Component({
    selector: 'winde-user-dialog',
    templateUrl: './user-dialog.component.html',
    styleUrls: ['./user-dialog.component.scss'],
    standalone: false
})
export class UserDialogComponent extends AbstractDialogComponent implements OnInit {
    public user: SuiManagedUserVM = new SuiManagedUserVM(this.data.payload.toDto() as ManagedUserVM);
    public passwordConfirmationField: SuiTypeString;
    public loaderKey = LoaderIds.USER_DIALOG_PRELOADER_ID;
    private readonly selectedUserAuthority: string = this.user.authorities[0].value;
    public isSikluUser: boolean = UserAuthorities.SIKLU_USERS.includes(this.selectedUserAuthority);
    public authorityField: SuiTypeRadio = new SuiTypeRadio(
        this.selectedUserAuthority,
        UserAuthorities.ALL_COMPANY,
        'userAuthoritiesEnum',
        ItemDirection.VERTICAL
    );

    constructor(
        private readonly principal: Principal,
        private readonly store: Store,
        private readonly actions$: Actions,
        private readonly dialogRef: MatDialogRef<GtthFolderDialogComponent>,
        public readonly preloaderService: PreloaderService,
        @Inject(MAT_DIALOG_DATA) public data: { payload: SuiUserDTO }
    ) {
        super();
    }

    ngOnInit(): void {
        this.actions$.pipe(ofType(closeUserDialog)).subscribe(() => {
            this.dialogRef.close();
        });

        [this.user.password, this.passwordConfirmationField] = createPasswordFieldsPair();

        this.user.password.isMandatory = !this.user.id.value;
        this.passwordConfirmationField.isMandatory = !this.user.id.value;

        this.authorityField.valueChangedCallback = (role) => this.user.authorities[0].setValue(role);

        this.user.login.isMandatory = true;

        this.principal.hasAnyAuthority(UserAuthorities.ALL_SIKLU).then((hasAuthority: boolean) => {
            this.user.login.readonly = Boolean(!hasAuthority && this.user.id.value);
        });
    }

    @HostListener('window:keydown.enter', ['$event'])
    onSave(event: KeyboardEvent): void {
        event.preventDefault();
        this.save();
    }

    public save(): void {
        if (!this.user.validateAndShowError() || !this.passwordConfirmationField.validateAndShowError()) {
            return;
        }
        const userClone = _cloneDeep(this.user);
        const action = isDefined(this.user.id.value) ? modifyUser({ updatedUser: userClone }) : createUser({ newUser: userClone });

        this.store.dispatch(action);
    }
}
